import {batch} from 'react-redux';
import moment from 'moment';
import {resetHeadState} from 'stores/actions/headStateAction';
import {restoreReteachPlateState} from 'stores/actions/reteachPlateAction';

import {resetVirtualTrayState} from 'stores/actions/virtualTrayAction';
import {restorePlateRecognitionState} from 'stores/actions/plateRecognitionAction';
import {selectIsInstrumentOperational} from 'stores/selectors/instrumentSelectors';
import {showModalType} from 'stores/actions/typedModalAction';
import {invokeAPI, resolveIntl} from './commonBusiness';
import {recoverRunningProtocol} from './runtimeBusiness';
import {isProtocolExecutor, saveState} from './stateBusiness';
import {
  fetchAvailableRoles,
  fetchAvailableRoleRights,
  isUserOperator,
  isAutoSaveProtocol,
} from './userManagementBusiness';
import {getLastDispenseReport} from './dispenseReportExplorerBusiness';
import {
  changeView,
  hideLoading,
  setDrawerVisibility,
  setHeadChannelsConfigOpen,
  showLoading,
  showSnackbar,
  restoreAppState,
  hideSnackbar,
} from '../actions/appStateAction';
import {
  resetCardState,
  resetCardTaskState,
  resetGroupState,
  restoreCardState,
  restoreCardTaskState,
  restoreGroupState,
} from '../actions/cardStateAction';
import {restoreReport} from '../actions/reportStateAction';
import {resetLabwareState, restoreLabwareState} from '../actions/labwareStateAction';
import {resetPlateTypeEditorState, restorePlateTypeEditorState} from '../actions/plateTypeEditorStateAction';
import {resetProtocolState, restoreProtocolState} from '../actions/protocolAction';
import {restoreNormalizationState, resetNormalizationState} from '../actions/normalizationStateAction';
import {resetRuntimeState, restoreRuntimeState} from '../actions/runtimeStateAction';
import {setSessionUser, setSessionId, resetSessionState} from '../actions/sessionAction';
import {resetTipState, restoreTipState} from '../actions/tipStateAction';
import {resetTemporaryState, restoreTemporaryState} from '../actions/temporaryStateAction';
import {resetTrayCalibrationState} from '../actions/trayCalibrationStateAction';
import {
  resetProtocolEditorState,
  restoreProtocolEditorState,
  setAutosaveDisabled,
  setAutosaveEnabled,
} from '../actions/protocolEditorAction';
import * as undoRedoAction from '../actions/undoRedoAction';
import {hideModal} from '../actions/modalStateAction';
import {getFastInfo, visionCompatibilityCheck} from './instrumentBusiness';
import {FastRobotErrorcode, Feedback, ModalType, SignalREvent, SignalRHub, ViewMode} from '../../constants/dataEnum';
import {getLicenseData} from './licenseBusiness';
import {
  clearRunningProtocol,
  getRunningProtocol,
  NEW_PROTOCOL_ID,
  openProtocol,
  processPendingSave,
} from './protocolManagementBusiness';
import {downloadDispenseReportCsv} from './reportStateBusiness';
import {restoreWizardState} from '../actions/wizardStateAction';
import {refreshLabwareDefinitions} from './labwareDatabaseBusiness';
import {SetupWizardStep} from '../../constants/SetupWizardEnum';
// eslint-disable-next-line import/namespace
import {gettingAndSetInstalledTray} from './trayCalibrationBusiness';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import {resetProtocolExplorerState} from '../actions/protocolExplorerAction';
import {fastCommonActuatorAPIErrorHandler} from './errorHandlerBusiness';
// eslint-disable-next-line import/no-cycle
import {showWhatsNewInFASTModal} from './softwareUpdaterBusiness';
import {fetchAndUpdateFavoriteLabwareIDs} from './userPreferenceBusiness';
import {checkIsInstrumentIdle} from './instrumentStatusBusiness';
import {checkAndSetIsTrayAttached} from './trayBusiness';
import {openTrayCalibrationPage} from './menuBusiness';
import {getInstrumentNonOperationalModalConfig} from './errorHandlers/serviceErrorBusiness';

const intl = resolveIntl();

export const resetState = (lastStateParam) => async (dispatch, getState) => {
  const state = getState();
  const {tipState} = state;

  let {showModal: showSetManualTipLocationModal} = tipState;

  if (lastStateParam) {
    const lastState = JSON.parse(lastStateParam);
    showSetManualTipLocationModal = lastState.tipState.showModal;
  }

  batch(async () => {
    if (showSetManualTipLocationModal) {
      await dispatch(invokeAPI(SignalRHub.AUTHORING, SignalREvent.ABORT_PROTOCOL, false, ''));
    }

    dispatch(resetCardState());
    dispatch(resetCardTaskState());
    dispatch(resetGroupState());
    dispatch(resetLabwareState());
    dispatch(resetPlateTypeEditorState());
    dispatch(resetProtocolState());
    dispatch(resetProtocolExplorerState());
    dispatch(resetRuntimeState());
    dispatch(resetTipState());
    dispatch(resetTemporaryState());
    dispatch(resetTrayCalibrationState());
    dispatch(resetProtocolEditorState());
    dispatch(resetNormalizationState());
    dispatch(resetHeadState());
    dispatch(resetVirtualTrayState());
    dispatch(saveState());
  });
};

const restoreAppStateFromSession = (lastAppState, lastRuntimeState, lastOpenedProtocol) => async (dispatch) => {
  const {viewMode, prevViewMode, snackbar} = lastAppState;
  const {reportVisible} = lastRuntimeState;

  const isOperator = dispatch(isUserOperator());

  const wasOnRuntime =
    viewMode === ViewMode.RUNTIME ||
    (isOperator && prevViewMode === ViewMode.RUNTIME) ||
    viewMode === ViewMode.SETUP_WIZARD;

  if (wasOnRuntime) {
    const isExecutor = await dispatch(isProtocolExecutor());

    if (isExecutor) {
      await dispatch(recoverRunningProtocol(reportVisible, viewMode));
    }

    if (viewMode !== ViewMode.SETUP_WIZARD) {
      let newViewMode = viewMode;

      if (isExecutor) {
        newViewMode = ViewMode.RUNTIME;
      } else if (lastOpenedProtocol && lastOpenedProtocol?.id !== NEW_PROTOCOL_ID) {
        const isAutosaveActive = await dispatch(isAutoSaveProtocol());
        await dispatch(openProtocol(lastOpenedProtocol, null, !isAutosaveActive));
        newViewMode = ViewMode.PROTOCOL_EDITOR;
      } else {
        dispatch(changeView(ViewMode.EXPLORER));
        newViewMode = ViewMode.EXPLORER;
      }

      // view still needs to be reassigned so it doesnt get overwritten when restoring appState
      lastAppState.viewMode = newViewMode;
    }
  }

  if (viewMode === ViewMode.ABOUT) {
    await dispatch(getFastInfo());
    await dispatch(getLicenseData());
  } else if (viewMode === ViewMode.TRAY_CALIBRATION) {
    await dispatch(openTrayCalibrationPage());
  }

  if (viewMode === ViewMode.SETUP_WIZARD) lastAppState.showDrawer = false;

  if (snackbar.show && snackbar?.text?.props) {
    lastAppState.snackbar.text = (
      <FormattedHTMLMessage id={snackbar.text.props.id} values={snackbar.text.props.values} />
    );
  }

  dispatch(restoreAppState(lastAppState));

  if (snackbar.show) setTimeout(() => dispatch(hideSnackbar()), 8000);
};

export const doRestoreState = (lastStateParam) => async (dispatch, getState) => {
  const {availableLabwareState} = getState();
  const {
    cardState,
    reportState,
    labwareState,
    plateTypeEditorState,
    protocolState,
    runtimeState,
    tipState,
    temporaryState,
    normalizationState,
    appState,
    protocolEditorState,
    undoRedoState,
    wizardState,
    reteachPlateState,
    plateRecognitionState,
  } = JSON.parse(lastStateParam);
  const {viewMode} = appState;
  const {stepName} = wizardState;
  const {cards, tasks} = cardState;
  const labwareStateToRestore = labwareState;

  batch(async () => {
    dispatch(showLoading(intl.formatMessage({id: 'login.LoginBoxContainer.restoringStateLoading'})));
    dispatch(restoreCardState(cards));
    dispatch(restoreCardTaskState(tasks));
    if (cardState.groups) dispatch(restoreGroupState(cardState.groups));
    dispatch(restoreLabwareState(labwareStateToRestore));
    dispatch(restoreReport(reportState));
    dispatch(restorePlateTypeEditorState(plateTypeEditorState));
    dispatch(restoreProtocolState(protocolState));
    dispatch(restoreRuntimeState(runtimeState));
    dispatch(restoreTipState(tipState));
    dispatch(restoreNormalizationState(normalizationState));
    dispatch(restoreTemporaryState(temporaryState));
    // Check the trayType from Back End
    await dispatch(gettingAndSetInstalledTray());
    dispatch(restoreProtocolEditorState(protocolEditorState));
    dispatch(undoRedoAction.restoreAll(undoRedoState));
    dispatch(restoreWizardState(wizardState));
    dispatch(restoreReteachPlateState(reteachPlateState));
    dispatch(restorePlateRecognitionState(plateRecognitionState));

    if (
      (viewMode === ViewMode.PROTOCOL_EDITOR || viewMode === ViewMode.RUNTIME) &&
      !Object.keys(availableLabwareState).length
    ) {
      await dispatch(refreshLabwareDefinitions());
    }

    await dispatch(checkAndSetIsTrayAttached());
    await dispatch(restoreAppStateFromSession(appState, runtimeState, protocolState.lastOpenedProtocol));

    if (stepName !== SetupWizardStep.PLATE_COPY_PROTOCOL) dispatch(hideLoading());

    dispatch(saveState(false));
  });
};

export const restoreState = (lastStateParam) => async (dispatch) => {
  try {
    await dispatch(doRestoreState(lastStateParam));
  } catch {
    await resetState();
    dispatch(changeView(ViewMode.EXPLORER));
  }
};

export const takeOverSession = (userResponse, isResetSession) => async (dispatch) => {
  const {userId} = userResponse;
  const isOperator = dispatch(isUserOperator());

  try {
    dispatch(showLoading());

    const newSessionResponse = await dispatch(invokeAPI(SignalRHub.SESSION, SignalREvent.CREATE_SESSION, userId));

    batch(async () => {
      dispatch(setSessionUser(userResponse));
      dispatch(setSessionId(newSessionResponse.sessionId));

      if (isResetSession && !isOperator) {
        const isAutosaveActive = await dispatch(isAutoSaveProtocol());

        if (isAutosaveActive) dispatch(setAutosaveEnabled());
        else dispatch(setAutosaveDisabled());

        dispatch(changeView(ViewMode.EXPLORER));
      }

      dispatch(hideLoading());

      dispatch(broadcastKickedOutByUserId(userId));
    });
  } catch (err) {
    console.log(err);

    dispatch(hideLoading());
  }
};

export const broadcastKickedOutByUserId = (userId) => async (dispatch) => {
  dispatch(invokeAPI(SignalRHub.SESSION, SignalREvent.BROADCAST_SESSION_CHANGED_EVENT, userId));
};

const shouldShowLoginConfirmation = (sessionContent, userId) => async (dispatch) => {
  let result = {
    show: true,
  };

  const ViewModeSkippingRestoreStateConfirmation = [
    ViewMode.SETUP_WIZARD,
    ViewMode.PLATE_TYPE_EDITOR,
    ViewMode.TRAY_CALIBRATION,
  ];

  if (sessionContent && sessionContent.length > 0) {
    const {appState} = JSON.parse(sessionContent);
    const isOperator = dispatch(isUserOperator());
    const isInstrumentIdle = await dispatch(checkIsInstrumentIdle());
    const runningProtocol = await dispatch(getRunningProtocol());

    if (
      (isOperator && !isInstrumentIdle && runningProtocol?.payload?.user?.userId === userId) ||
      ViewModeSkippingRestoreStateConfirmation.includes(appState.viewMode)
    ) {
      console.log('Restoring state...');
      result = {show: false, restoreState: true};
    } else if (appState.viewMode === ViewMode.EXPLORER) {
      result = {show: false, restoreState: false};
    }
  } else {
    result = {show: false};
  }
  return result;
};

export const deleteSession = (userId) => async (dispatch) => {
  await dispatch(invokeAPI(SignalRHub.SESSION, SignalREvent.DELETE_SESSION, userId));
};

const downloadReportIfAvailable =
  (
    userResponse,
    // eslint-disable-next-line default-param-last
    isDownloadComplete = true,
    // eslint-disable-next-line default-param-last
    forceDownload = false,
    sessionContent,
  ) =>
  async (dispatch) => {
    let lastSessionViewMode;

    if (sessionContent) {
      const {appState} = JSON.parse(sessionContent);
      lastSessionViewMode = appState.viewMode;
    }

    const report = await dispatch(getLastDispenseReport(false, isDownloadComplete, userResponse.userId));
    console.log('report', report);

    if (report) {
      const {name: protocolRunBy} = report.user ?? {};

      const fileName = `Dispense Report${protocolRunBy ? `_${protocolRunBy}` : ''}_${report.name}_${moment.utc(report.createdTime).local().format('YYYY-MM-DD HH:mm:ss')}.csv`;

      await dispatch(downloadDispenseReportCsv(false, report, fileName));
      dispatch(
        showSnackbar(
          Feedback.INFO,
          intl.formatMessage({id: 'dispenseReportExplorer.autoDownload.notification.success'}),
        ),
      );
    } else if (!forceDownload) {
      // Download last dispense report if there is a case where the isCompleted field doesn't get flagged as true
      // but the protocol has finished running

      const isInstrumentIdle = await dispatch(checkIsInstrumentIdle());
      const runningProtocolResponse = await dispatch(getRunningProtocol());

      if (runningProtocolResponse?.payload) {
        if (isInstrumentIdle && runningProtocolResponse.payload.user?.userId === userResponse?.userId) {
          dispatch(downloadReportIfAvailable(userResponse, false, true, sessionContent));
        }
      }
    }

    if (
      forceDownload &&
      !report &&
      lastSessionViewMode !== ViewMode.SETUP_WIZARD &&
      lastSessionViewMode !== ViewMode.RUNTIME
    ) {
      await dispatch(clearRunningProtocol());
    }
  };

export const login = (username, password) => async (dispatch, getState) => {
  let user;
  let session;
  let loginConfirmation = {show: false};
  let visionCompatibility = FastRobotErrorcode.NoError;

  try {
    const userResponse = await dispatch(invokeAPI(SignalRHub.USER_MANAGEMENT, SignalREvent.LOGIN, username, password));

    if (!userResponse) {
      batch(() => {
        dispatch(hideLoading());
        dispatch(
          showSnackbar(
            Feedback.WARNING,
            intl.formatMessage({id: 'login.LoginBoxContainer.loginInvalidUsernamePassword'}),
          ),
        );
      });

      return null;
    }

    // check update status
    const {signalrState} = getState();
    const isInstrumentOperational = selectIsInstrumentOperational(getState());

    if (signalrState[SignalRHub.SOFTWARE_UPDATER] && isInstrumentOperational) {
      const updateStatusResponse = await dispatch(
        invokeAPI(SignalRHub.SOFTWARE_UPDATER, SignalREvent.GET_SOFTWARE_UPDATE_STATUS),
      );

      if (
        updateStatusResponse &&
        updateStatusResponse.isUpdating &&
        updateStatusResponse.userId !== userResponse.userId
      ) {
        batch(() => {
          dispatch(hideLoading());
          dispatch(showSnackbar(Feedback.ERROR, intl.formatMessage({id: 'about.updaterNotification.loginValidation'})));
        });

        return null;
      }
    }
    if (isInstrumentOperational) await dispatch(getFastInfo(false));

    await dispatch(setSessionUser(userResponse));
    await dispatch(fetchAvailableRoles());
    await dispatch(fetchAvailableRoleRights(userResponse.userRole));
    await dispatch(fetchAndUpdateFavoriteLabwareIDs());

    let visionCompatibilityResponse = '';
    const sessionResponse = await dispatch(
      invokeAPI(SignalRHub.SESSION, SignalREvent.GET_SESSION, userResponse.userId),
    );

    if (isInstrumentOperational) {
      visionCompatibilityResponse = await dispatch(visionCompatibilityCheck());

      if (sessionResponse) {
        loginConfirmation = await dispatch(shouldShowLoginConfirmation(sessionResponse?.content, userResponse.userId));
        await dispatch(downloadReportIfAvailable(userResponse, true, false, sessionResponse?.content));
      }
    }

    user = userResponse;
    session = sessionResponse;
    visionCompatibility = visionCompatibilityResponse;

    dispatch(hideLoading());
  } catch (err) {
    console.log(err);

    dispatch(hideLoading());

    return null;
  }

  return {
    user,
    session,
    loginConfirmation,
    visionCompatibility,
  };
};

export const logout = () => async (dispatch, getState) => {
  const state = getState();
  const {sessionState} = state;
  const {userId} = sessionState.user;
  const isOperator = dispatch(isUserOperator());
  const isInstrumentIdle = await dispatch(checkIsInstrumentIdle());
  const runningProtocol = await dispatch(getRunningProtocol());

  const doLogout = () => {
    if (userId) {
      batch(() => {
        // change view to login first before resetting state
        dispatch(changeView(ViewMode.LOGIN));

        if (
          !isOperator ||
          (isOperator && isInstrumentIdle) ||
          (isOperator && runningProtocol?.payload?.user?.userId !== userId)
        ) {
          dispatch(deleteSession(userId));
          dispatch(resetState());
        }
      });
    }
  };

  dispatch(processPendingSave(null, doLogout, false, false));
};

export const kickSession = () => (dispatch) => {
  batch(() => {
    dispatch(hideModal());
    dispatch(setDrawerVisibility(false));
    dispatch(setHeadChannelsConfigOpen(false));
    dispatch(showLoading(intl.formatMessage({id: 'login.LoginBoxContainer.sessionTakenLoading'})));

    dispatch(resetState());
    dispatch(resetSessionState());
    dispatch(changeView(ViewMode.LOGIN));

    setTimeout(() => {
      dispatch(hideLoading());
      dispatch(
        showSnackbar(Feedback.SUCCESS, intl.formatMessage({id: 'login.LoginBoxContainer.sessionTakenNotification'})),
      );
    }, 2000);
  });
};

export const updateSessionActiveStatus = (isActive) => async (dispatch, getState) => {
  const state = getState();
  const {sessionState} = state;
  const {userId} = sessionState.user;

  if (!userId) {
    return;
  }

  await dispatch(invokeAPI(SignalRHub.SESSION, SignalREvent.UPDATE_SESSION_ACTIVE_STATUS, userId, isActive));
};

export const setAutosaveProtocolState = () => async (dispatch) => {
  const isOperator = dispatch(isUserOperator());
  const isAutosaveActive = await dispatch(isAutoSaveProtocol());

  if (isAutosaveActive || isOperator) await dispatch(setAutosaveEnabled());
  else await dispatch(setAutosaveDisabled());
};

export const proceedNewState = (response) => async (dispatch, getState) => {
  const isInstrumentOperational = selectIsInstrumentOperational(getState());
  const {user, session, visionCompatibility} = response;
  const isOperator = dispatch(isUserOperator());

  await dispatch(takeOverSession(user, true));
  if (session?.content) {
    await dispatch(resetState(session.content));
  }
  if (isOperator) {
    dispatch(changeView(ViewMode.EXPLORER));
  }
  await dispatch(setAutosaveProtocolState());

  if (visionCompatibility !== FastRobotErrorcode.NoError && isInstrumentOperational) {
    dispatch(fastCommonActuatorAPIErrorHandler(visionCompatibility));
  } else {
    const instrumentModalConfig = getInstrumentNonOperationalModalConfig();
    const dontShowInstrumentNonOperationalModal = Boolean(instrumentModalConfig[user?.userId]);
    const onAfterClose = () => {
      if (!isInstrumentOperational && !dontShowInstrumentNonOperationalModal) {
        dispatch(showModalType(ModalType.INSTRUMENT_NON_OPERATIONAL));
      }
    };

    const isModalShown = await dispatch(showWhatsNewInFASTModal(onAfterClose));

    if (!isModalShown) {
      onAfterClose();
    }
  }
};
